import React, { useEffect } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { getAuthStatus } from '../utils/auth';

/**
 * Component to protect routes that require authentication
 * Redirects to login if not authenticated
 * Can require specific roles for access
 */
const RequireAuth = ({ children, requiredRole = null }) => {
  const { isAuthenticated, user, isLoading, updateAuthState } = useAuth();
  const location = useLocation();
  
  // We no longer need to manually refresh auth status in RequireAuth
  // The AuthContext now handles this with a proper interval check
  
  // Show loading state
  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-indigo-700"></div>
      </div>
    );
  }
  
  // If not authenticated, redirect to login
  if (!isAuthenticated) {
    // Save the current location to redirect back after login
    return <Navigate to="/login" state={{ from: location }} replace />;
  }
  
  // If role check is required
  if (requiredRole) {
    // Admin can access everything
    if (user.role === 'admin') {
      return children;
    }
    
    // Read_write can access read and read_write endpoints
    if (requiredRole === 'read' && (user.role === 'read_write' || user.role === 'read')) {
      return children;
    }
    
    // Read_write can access read_write endpoints
    if (requiredRole === 'read_write' && user.role === 'read_write') {
      return children;
    }
    
    // If none of the above, redirect to home with no permission
    return <Navigate to="/" replace />;
  }
  
  // If authenticated and no role check or role check passed
  return children;
};

export default RequireAuth;